import { API_URL } from '../config';

// Sections of the members area. Keep ids in sync with backend/members.js.
export const MEMBER_SECTIONS = [
  {
    id: 'board',
    title: 'EBTA Board & official documents',
    short: 'Board & documents',
    description: 'Meeting reports, statutes, internal rules and other official documents.',
  },
  {
    id: 'conferences',
    title: 'EBTA Conferences',
    short: 'Conferences',
    description: 'Presentations, articles and photo galleries from EBTA conferences.',
  },
  {
    id: 'summercamps',
    title: 'EBTA Summer Camps',
    short: 'Summer Camps',
    description: 'Articles, presentations and photo galleries from the Summer Camps.',
  },
  {
    id: 'meetlearn',
    title: 'EBTA Meet & Learn',
    short: 'Meet & Learn',
    description: 'Recordings of Meet & Learn sessions with their topic and guest lecturer.',
  },
  {
    id: 'directory',
    title: 'Directory of EBTA Members',
    short: 'Member directory',
    description: 'Find and contact fellow EBTA members who chose to be listed.',
  },
];

export const getSection = (id) => MEMBER_SECTIONS.find((s) => s.id === id) || null;

const memberToken = () => (window.Outseta && window.Outseta.getAccessToken && window.Outseta.getAccessToken()) || '';
const adminToken = () => sessionStorage.getItem('adminToken') || '';

async function request(path, { token, method = 'GET', body } = {}) {
  const res = await fetch(`${API_URL}/api/members${path}`, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  });
  if (res.status === 204) return null;
  const isJson = (res.headers.get('content-type') || '').includes('application/json');
  const data = isJson ? await res.json() : null;
  if (!res.ok) {
    const err = new Error((data && data.error) || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

async function fetchBlobUrl(path, token) {
  const res = await fetch(`${API_URL}/api/members${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const err = new Error(`Could not load file (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return URL.createObjectURL(await res.blob());
}

// Member-facing calls (authenticated with the Outseta access token)
export const fetchSectionItems = (section) => request(`/items?section=${encodeURIComponent(section)}`, { token: memberToken() });
export const fetchDirectory = () => request('/directory', { token: memberToken() });
export const fetchMemberFileUrl = (itemId, fileId) => fetchBlobUrl(`/items/${itemId}/files/${fileId}`, memberToken());

// Admin calls (authenticated with the existing EBTA admin login)
export const adminListItems = (section) => request(`/admin/items?section=${encodeURIComponent(section)}`, { token: adminToken() });
export const adminCreateItem = (formData) => request('/admin/items', { token: adminToken(), method: 'POST', body: formData });
export const adminUpdateItem = (id, formData) => request(`/admin/items/${id}`, { token: adminToken(), method: 'PUT', body: formData });
export const adminDeleteItem = (id) => request(`/admin/items/${id}`, { token: adminToken(), method: 'DELETE' });
export const adminDeleteFile = (id, fileId) => request(`/admin/items/${id}/files/${fileId}`, { token: adminToken(), method: 'DELETE' });
export const adminFileUrl = (itemId, fileId) => fetchBlobUrl(`/admin/items/${itemId}/files/${fileId}`, adminToken());

// Turns a YouTube or Vimeo link into an embeddable player URL, or null.
export function toEmbedUrl(url) {
  if (!url) return null;
  let u;
  try {
    u = new URL(url);
  } catch (e) {
    return null;
  }
  const host = u.hostname.replace(/^www\./, '');

  if (host === 'youtube.com' || host === 'm.youtube.com') {
    const id = u.searchParams.get('v');
    return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
  }
  if (host === 'youtu.be') {
    const id = u.pathname.slice(1);
    return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
  }
  if (host === 'vimeo.com') {
    // vimeo.com/123456789 or unlisted vimeo.com/123456789/abcdef1234
    const [id, hash] = u.pathname.split('/').filter(Boolean);
    if (!/^\d+$/.test(id || '')) return null;
    return hash ? `https://player.vimeo.com/video/${id}?h=${hash}` : `https://player.vimeo.com/video/${id}`;
  }
  if (host === 'player.vimeo.com') return url;
  return null;
}

export const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
